/* eslint-disable no-console */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Joyride, STATUS } from "react-joyride";
import { useQueryClient } from "@tanstack/react-query";
import DashboardScreen from "../../components/screens/Dashboard/DashboardScreen";
import ShareDrawer from "@nualang/nualang-ui-components/dist/Misc/ShareButton/ShareDrawer";
import AvatarDialog from "@nualang/nualang-ui-components/dist/Dialogs/AvatarDialog/AvatarDialog";
import {
  courses as courseQuerys,
  completions as completionQuerys,
  users as userQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MembersImage from "../../img/members.svg";
import config from "../../config";

import { getCourses } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { UploadUserImage } from "../../utils/imageUpload";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import {
  getCourseMember,
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import { getCategories } from "@nualang/nualang-api-and-queries/APIs/Categories.js";
import { getMemberCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import {
  courses as courseHelpers,
  completions as completionHelpers,
  users as userHelpers,
} from "../../utils/helpers";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { useApp, useCourse, useSubscription } from "../../hooks";

const { languages, stage } = config;

function Dashboard(props) {
  const { verificationStatus } = props;
  const { t } = useTranslation();
  const [confirm] = useConfirm(t);
  const theme = useTheme();
  // The Contact side-nav item lives in ResponsiveDrawer's permanent drawer.
  // Below md only the temporary drawer renders, and it is keepMounted, so
  // #contact is in the DOM but hidden behind a closed modal.
  const isPermanentDrawerVisible = useMediaQuery(theme.breakpoints.up("md"));
  const queryClient = useQueryClient();
  const { authData, navigate, handleSignIn } = props;
  const {
    openSnackbar,
    handleOpenModerateDialog,
    startLoading,
    stopLoading,
    user: { attributes = {} },
    setUser
  } = useApp();
  const { subscription } = useSubscription();

  const { username } = authData;

  const [isPictureDialogOpen, setIsPictureDialogOpen] = useState(false);
  const [shareScreenOpen, setShareScreenOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [avatarOptionClicked, setAvatarOptionClicked] = useState(false);
  const duplicateCourseMutation = courseQuerys.useDuplicateCourse(
    courseHelpers.duplicateCourse,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data) => {
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.list({
            createdBy: username,
            verbose: false,
            // Get all using high limit
            limit: config.limits.courses,
          }),
        });
        if (data && data.Item && data.Item.courseId) {
          const { courseId } = data.Item;
          if (courseId) {
            stopLoading();
            openSnackbar("created", "success");
            navigate(`/courses/${courseId}`);
          }
        }
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const joyrideSteps = [
    {
      content: (
        <div>
          <h2>
            {t("dashboard_welcome", {
              name: attributes ? attributes.preferred_username : "",
            })}
          </h2>
          <br />
          <p>{t("dashboard_welcome_desc")}</p>
        </div>
      ),
      placement: "center",
      target: "body",
    },
    {
      content: (
        <div>
          <h2>{t("search_nualang")}</h2>
          <br />
          <p>{t("search_nualang_desc")}</p>
        </div>
      ),
      spotlightPadding: 8,
      target: "#appbar-search",
    },
    {
      content: (
        <div>
          <h2>{t("create_nualang")}</h2>
          <br />
          <p>{t("create_nualang_desc")}</p>
        </div>
      ),
      spotlightPadding: 8,
      target: "#appbar-create-button",
    },
    ...(isPermanentDrawerVisible
      ? [
          {
            content: (
              <div>
                <h2>{t("get_in_touch")}</h2>
                <br />
                <p>{t("get_in_touch_desc")}</p>
              </div>
            ),
            spotlightPadding: 8,
            target: "#contact",
          },
        ]
      : []),
  ];

  const [joyrideState, setJoyrideState] = useState({
    run: false,
    steps: joyrideSteps,
  });

  const { setIsJoinCourseDialogOpen, setNewCourse } = useCourse();


  // Query for Most Popular Courses
  const popularCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      // Filter out courses without exerciseStartTotal or with 0 plays
      const filteredCourses = formattedCourses.filter(
        (course) => course.exerciseStartTotal > 0,
      );
      return filteredCourses;
    },
    {
      filters: {
        visibility: "public",
        learnLang: attributes["custom:learnLang"] || null,
        verbose: false,
        limit: config.limits.popularCourses,
        sort: "popular",
      },
    },
  );

  const newCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      formattedCourses.sort((a, b) => b.createdAt - a.createdAt);
      return formattedCourses;
    },
    {
      filters: {
        limit: config.limits.newCourses,
        visibility: "public",
        learnLang: attributes["custom:learnLang"] || null,
        verbose: false,
        minExerciseStartTotal: 1,
      },
    },
  );

  const handleSelectCategory = (searchTerm, sort = "verified") => {
    const encodedSearchTerm = searchTerm.replaceAll(" ", "+");
    if (encodedSearchTerm === "nuala+news") {
      navigate(`/search?searchText=${encodedSearchTerm}&sort=newest`);
    } else {
      navigate(`/search?searchText=${encodedSearchTerm}&sort=${sort}`);
    }
  };

  const memberCompletionsQuery = completionQuerys.useMemberCompletions(
    async (memberId) => {
      const memberCompletions = await getMemberCompletions(memberId);
      const formattedCompletions = await completionHelpers.formatCompletions(
        memberCompletions.Items,
        memberId,
        attributes.preferred_username,
      );
      return formattedCompletions;
    },
    {
      memberId: username,
    },
    { enabled: !!username },
  );

  //********* What is this code doing? seems to assign to an undefined variable *******/
  /* eslint-disable */
  if (
    attributes["custom:learnLang"] &&
    languages &&
    languages[attributes["custom:learnLang"]].recommended &&
    languages[attributes["custom:learnLang"]].recommended[stage] &&
    languages[attributes["custom:learnLang"]].recommended[stage].courses &&
    languages[attributes["custom:learnLang"]].recommended[stage].courses.length
  ) {
    recommendedCourseIds =
      languages[attributes["custom:learnLang"]].recommended[stage].courses;
  }
  /* eslint-enable */
  //********* What is this code doing? seems to assign to an undefined variable *******/

  const recommendedCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      formattedCourses.sort((a, b) => b.createdAt - a.createdAt);
      return formattedCourses;
    },
    {
      filters: {
        language: attributes["custom:learnLang"],
        verbose: false,
        limit: config.limits.courses,
      },
    },
    {
      enabled: !!attributes["custom:learnLang"],
    },
  );

  useEffect(() => {
    if (localStorage.getItem(`dashboard-tour-completed-${username}`)) {
      return undefined;
    }

    let scrollTimeoutId = null;

    if (isPermanentDrawerVisible) {
      scrollTimeoutId = setTimeout(() => {
        const contactElement = document.getElementById("contact");
        if (contactElement) {
          contactElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 1000);
    }

    // Take a fresh copy of the steps rather than the ones snapshotted into state on
    // first render. The Contact step only exists above md, and below md #contact is
    // still mounted inside the closed temporary drawer, so a stale list would leave
    // joyride waiting on a hidden target and then skipping the step.
    setJoyrideState((prevState) => ({
      ...prevState,
      run: true,
      steps: joyrideSteps,
    }));

    return () => {
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
    // Only re-run the effect if the username, user attributes or drawer visibility changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, attributes, isPermanentDrawerVisible]);

  // Mutations
  const updateUserMutation = userQuerys.useUpdateUser(
    userHelpers.updateUserAttributes,
    {
      onSuccess: (data) => {
        setUser(data);
        openSnackbar("user_updated", "success");
        queryClient.invalidateQueries({
          queryKey: userQuerys.userKeys.item(username),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      navigate(`/courses/${variables.courseId}`);
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const handleUpdateUser = (values) => {
    updateUserMutation.mutate({ userId: username, values });
  };

  const saveAvatarToDynamo = (avatarAttributes) => {
    const value = {
      avatar: avatarAttributes,
    };
    handleUpdateUser(value);
  };

  const handleSearch = () => navigate("/search");

  const handleStartCourse = async (course) => {
    if (!username) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId: course.courseId, username });
      }
    }
  };

  const handleLeaveCourse = (courseId) => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleViewCourseTopic = (cId, sectionId, topicId) => {
    navigate(`/courses/${cId}/${sectionId}/${topicId}`);
  };

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const getMemberDetails = (members) => userHelpers.fetchMembersDetails(members);

  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "close") {
      setJoyrideState((prevState) => ({
        ...prevState,
        run: false,
      }));
      localStorage.setItem(`dashboard-tour-completed-${username}`, "true");
      // If a user has not selected a picture ask them to select one
      if (!attributes || !attributes.picture) {
        setIsPictureDialogOpen(true);
      }
    }
  };

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const userInitialValues = {
    email: attributes.email,
    preferred_username: attributes.preferred_username,
    "custom:forLang": attributes["custom:forLang"] || "",
    "custom:learnLang": attributes["custom:learnLang"] || "",
    picture: attributes.picture || "",
  };

  const handleDuplicateCourse = async (values) => {
    startLoading();
    await duplicateCourseMutation.mutate({ values });
  };

  const { run, steps } = joyrideState;

  return (
    <>
      {run && (
        <Joyride
          onEvent={handleJoyrideCallback}
          continuous={true}
          run={run}
          scrollToFirstStep={true}
          steps={steps}
          locale={{
            back: t("back"),
            close: t("close"),
            last: t("last"),
            next: t("next"),
            nextWithProgress: t("next_with_progress"),
            skip: t("skip"),
          }}
          options={{
            showProgress: true,
            skipScroll: true,
            zIndex: 10000,
            arrowColor: theme.palette.background.paper,
            backgroundColor: theme.palette.background.paper,
            primaryColor: theme.palette.primary.main,
            textColor: theme.palette.text.primary,
            buttons: ['back', 'close', 'primary', 'skip']
          }} />
      )}
      <DashboardScreen
        t={t}
        categoryTypesArray={
          config.languages[attributes["custom:learnLang"].toLowerCase()]
            .categoryTypesArray || []
        }
        learnLang={attributes["custom:learnLang"].toLowerCase()}
        getCategories={getCategories}
        handleSelectCategory={handleSelectCategory}
        handleSearch={handleSearch}
        memberPlaceholderImageUrl={MembersImage}
        isNewCoursesLoading={newCoursesQuery.isLoading}
        isRecommendedCoursesLoading={recommendedCoursesQuery.isLoading}
        isPopularCoursesLoading={popularCoursesQuery.isLoading}
        recommendedCourses={
          recommendedCoursesQuery.isSuccess &&
          Array.isArray(recommendedCoursesQuery.data) &&
          recommendedCoursesQuery.data.length
            ? recommendedCoursesQuery.data
            : []
        }
        newCourses={
          newCoursesQuery.isSuccess &&
          Array.isArray(newCoursesQuery.data) &&
          newCoursesQuery.data.length
            ? newCoursesQuery.data
            : []
        }
        popularCourses={
          popularCoursesQuery.isSuccess &&
          Array.isArray(popularCoursesQuery.data) &&
          popularCoursesQuery.data.length
            ? popularCoursesQuery.data
            : []
        }
        completions={
          memberCompletionsQuery.isSuccess &&
          Array.isArray(memberCompletionsQuery.data) &&
          memberCompletionsQuery.data.length
            ? memberCompletionsQuery.data
            : []
        }
        handleViewCourseTopic={handleViewCourseTopic}
        handleStartCourse={handleStartCourse}
        handleLeaveCourse={handleLeaveCourse}
        handleViewCourse={handleViewCourse}
        getMemberDetails={getMemberDetails}
        getCourseSections={getCourseSections}
        memberId={username}
        getCourseMember={getCourseMember}
        getCourseMembers={getCourseMembers}
        username={username}
        membersProps={{
          handleViewActivity,
        }}
        handleViewUserProfile={handleViewUserProfile}
        handleDuplicateCourse={handleDuplicateCourse}
      />
      <ShareDrawer
        t={t}
        url={shareUrl}
        open={shareScreenOpen}
        handleClose={() => setShareScreenOpen(false)}
        subscription={subscription}
      />
      {!run && (
        <AvatarDialog
          t={t}
          dialogTitle={t("update_picture")}
          open={isPictureDialogOpen}
          handleClose={() => setIsPictureDialogOpen(false)}
          name="picture"
          value={userInitialValues.picture}
          userRole={
            attributes["custom:role"] ? attributes["custom:role"] : "learn"
          }
          handleChange={async (e) => {
            const { value } = e.target;
            const values = userInitialValues;
            if (value && value !== userInitialValues.picture) {
              values.pictureWarnDetails = {};
              const base64String = value.split(",");
              const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
                await UploadUserImage(
                  base64String[1] || base64String[0],
                  username,
                );
              if (moderated || warn) {
                if (verificationStatus === "approved" || warn) {
                  const confirmation = await confirm(
                    `${t("warning")}`,
                    `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
                    `${t("use_anyways")}`,
                  );
                  if (confirmation !== true) {
                    return;
                  }
                  values.pictureWarnDetails = {
                    user: username,
                    labels: moderatedLabels.join(", "),
                  };
                } else {
                  const moderationReason = moderatedLabels[0];
                  handleOpenModerateDialog(moderationReason);
                  return;
                }
              }
              if (picture) {
                values.picture = picture;
                values.pictureS3Key = pictureS3Key;
              } else {
                delete values.picture;
              }
            }
            handleUpdateUser(values);
            setIsPictureDialogOpen(false);
          }}
          saveAvatarToDynamo={saveAvatarToDynamo}
          avatarOptionSelected={avatarOptionClicked}
          setAvatarOptionClicked={setAvatarOptionClicked}
        />
      )}
    </>
  );
}

Dashboard.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
};

export default Dashboard;
